import React from 'react';
import { ErrorBox } from '../classes/pdStyle';
import Buffering from './Buffering';
import dsd from '../classes/clsDSD';

class Rendition extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            renderState: null,
            renditionsSpec: null,
            representation: props.representation || null,
            format: props.format || null,
            options: {},
            result: null,
            resultIsObject: false,
            errorMessage: null,
        };

        this.dsd = new dsd();
        this.dsd.StateObject = this;

        this.handleRepresentationChange = this.handleRepresentationChange.bind(this);
        this.handleFormatChange         = this.handleFormatChange.bind(this);
        this.handleOptionChange         = this.handleOptionChange.bind(this);
    }

    componentDidMount() {
        this.loadRenditions();
    }

    componentDidUpdate(prevProps) {
        const p = this.props;
        const pp = prevProps;
        if (p.endpoint !== pp.endpoint || p.id !== pp.id ||
            p.representation !== pp.representation || p.format !== pp.format) {
            this.setState({
                renditionsSpec: null,
                representation: p.representation || null,
                format: p.format || null,
                options: {},
                result: null,
                resultIsObject: false,
                errorMessage: null,
                renderState: null,
            }, () => this.loadRenditions());
        }
    }

    loadRenditions() {
        const { endpoint } = this.props;
        if (!endpoint) { return; }

        const headers = { headers: { 'Content-type': 'application/json' } };
        const authHeader = this.dsd.authHeader();
        if (authHeader) { headers.headers.Authorization = authHeader.Authorization; }

        this.setState({ renderState: 'loading' });

        this.dsd.httpConfig.get(`${endpoint}/renditions`, headers)
            .then(response => {
                const spec = response.data;
                this.setState({ renditionsSpec: spec }, () => {
                    // Resolve initial representation
                    let rep = this.state.representation;
                    if (!rep) { rep = spec.defaultRepresentation; }
                    // Resolve initial format for that representation
                    const repSpec = spec.representations && spec.representations[rep];
                    let fmt = this.state.format;
                    if (!fmt && repSpec) { fmt = repSpec.defaultFormat; }
                    const opts = {};
                    if (repSpec && repSpec.options) {
                        for (const [key, def] of Object.entries(repSpec.options)) {
                            opts[key] = def.default ?? false;
                        }
                    }
                    this.setState({ representation: rep, format: fmt, options: opts, renderState: 'loaded' }, () => {
                        this.fetchResult();
                    });
                });
            })
            .catch(e => {
                this.setState({ renderState: 'error', errorMessage: e.message });
            });
    }

    fetchResult() {
        const { endpoint, id } = this.props;
        const { representation, format, options } = this.state;

        if (!endpoint || !id || !representation || !format) { return; }

        const headers = { headers: { 'Content-type': 'application/json' } };
        const authHeader = this.dsd.authHeader();
        if (authHeader) { headers.headers.Authorization = authHeader.Authorization; }

        this.setState({ result: null, resultIsObject: false, renderState: 'fetching' });

        const optParams = Object.entries(options || {})
            .filter(([, v]) => v !== false && v !== undefined && v !== null && v !== '')
            .map(([k, v]) => v === true ? `${k}=true` : `${k}=${encodeURIComponent(v)}`)
            .join('&');
        const url = `${endpoint}/${id}?representation=${encodeURIComponent(representation)}&format=${encodeURIComponent(format)}${optParams ? `&${optParams}` : ''}`;

        this.dsd.httpConfig.get(url, headers)
            .then(response => {
                const data = response.data;
                const isObject = data !== null && typeof data === 'object';
                this.setState({
                    result: isObject ? JSON.stringify(data, null, 2) : String(data),
                    resultIsObject: isObject,
                    renderState: 'loaded',
                    errorMessage: null,
                });
            })
            .catch(e => {
                const msg = (e.response && e.response.data && e.response.data.message)
                    ? e.response.data.message
                    : e.message;
                this.setState({ renderState: 'error', errorMessage: msg });
            });
    }

    handleRepresentationChange(event) {
        const rep = event.target.value;
        const { renditionsSpec } = this.state;
        const repSpec = renditionsSpec && renditionsSpec.representations && renditionsSpec.representations[rep];
        const fmt = repSpec ? repSpec.defaultFormat : null;
        const opts = {};
        if (repSpec && repSpec.options) {
            for (const [key, def] of Object.entries(repSpec.options)) {
                opts[key] = def.default ?? false;
            }
        }
        this.setState({ representation: rep, format: fmt, options: opts }, () => this.fetchResult());
    }

    handleFormatChange(event) {
        const fmt = event.target.value;
        this.setState({ format: fmt }, () => this.fetchResult());
    }

    handleOptionChange(key, value) {
        this.setState(prev => ({ options: { ...prev.options, [key]: value } }), () => this.fetchResult());
    }

    render() {
        const { representation: propRep, format: propFmt } = this.props;
        const { renderState, renditionsSpec, representation, format, options, result, resultIsObject, errorMessage } = this.state;

        let jsonResult = null;
        if (resultIsObject) {
            try {
                jsonResult = JSON.parse(result);
            } catch (e) {
                console.error('Failed to parse JSON result:', e);
            }
        }

        if (renderState === 'loading') { return <Buffering />; }

        if (renderState === 'error' && !renditionsSpec) {
            return <ErrorBox>{errorMessage || 'Failed to load renditions'}</ErrorBox>;
        }

        if (!renditionsSpec) { return null; }

        const repOptions = Object.keys(renditionsSpec.representations || {});
        const repSpec = representation && renditionsSpec.representations && renditionsSpec.representations[representation];
        const fmtOptions = repSpec ? Object.keys(repSpec.formats || {}) : [];

        return (
            <div>
                <div style={{ display: 'flex', gap: '1.5em', alignItems: 'flex-start', marginBottom: '0.75em', flexWrap: 'wrap' }}>
                    {!propRep && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25em' }}>
                            <label style={{ fontWeight: 'bold' }}>Representation</label>
                            <select
                                value={representation || ''}
                                onChange={this.handleRepresentationChange}
                            >
                                {repOptions.map(r => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {!propFmt && fmtOptions.length > 1 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25em' }}>
                            <label style={{ fontWeight: 'bold' }}>Format</label>
                            <select
                                value={format || ''}
                                onChange={this.handleFormatChange}
                            >
                                {fmtOptions.map(f => (
                                    <option key={f} value={f}>{f}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {repSpec && repSpec.options && Object.keys(repSpec.options).length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25em' }}>
                            <label style={{ fontWeight: 'bold' }}>Options</label>
                            {Object.entries(repSpec.options).map(([key, def]) => (
                                def.values
                                    ? (
                                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.4em', fontSize: '0.9em' }} title={def.description || ''}>
                                            {def.label || key}
                                            <select
                                                value={(options && options[key] != null) ? options[key] : (def.default || def.values[0])}
                                                onChange={e => this.handleOptionChange(key, e.target.value)}
                                            >
                                                {def.values.map(v => (
                                                    <option key={v} value={v}>{v}</option>
                                                ))}
                                            </select>
                                        </label>
                                    )
                                    : (
                                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.4em', fontSize: '0.9em', cursor: 'pointer' }} title={def.description || ''}>
                                            <input
                                                type="checkbox"
                                                checked={!!(options && options[key])}
                                                onChange={e => this.handleOptionChange(key, e.target.checked)}
                                            />
                                            {def.label || key}
                                        </label>
                                    )
                            ))}
                        </div>
                    )}
                    {repSpec && format && repSpec.formats && repSpec.formats[format] && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25em' }}>
                            <label style={{ fontWeight: 'bold' }}>Content Type</label>
                            <span style={{ fontSize: '0.85em', color: '#666' }}>
                                {repSpec.formats[format].contentType}
                            </span>
                        </div>
                    )}
                    {this.props.id && representation && format && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25em' }}>
                            <label style={{ fontWeight: 'bold' }}>API</label>
                            <span style={{ fontSize: '0.85em', color: '#666', fontFamily: 'monospace' }}>
                                {(() => {
                                    const isDefaultRep = representation === (renditionsSpec && renditionsSpec.defaultRepresentation);
                                    const isDefaultFmt = format === (repSpec && repSpec.defaultFormat);
                                    const params = [];
                                    if (!isDefaultRep) { params.push(`representation=${representation}`); }
                                    if (!isDefaultFmt) { params.push(`format=${format}`); }
                                    Object.entries(options || {}).forEach(([k, v]) => {
                                        if (v !== false && v !== undefined && v !== null && v !== '') {
                                            params.push(v === true ? `${k}=true` : `${k}=${encodeURIComponent(v)}`);
                                        }
                                    });
                                    const qs = params.length ? `?${params.join('&')}` : '';
                                    return `${this.props.endpoint}/${this.props.id}${qs}`;
                                })()}
                            </span>
                        </div>
                    )}
                </div>

                {renderState === 'fetching' && <Buffering />}

                {renderState === 'error' && errorMessage && (
                    <ErrorBox>{errorMessage}</ErrorBox>
                )}

                {representation === 'xsd' && jsonResult
                    ? Object.keys(jsonResult).map((filename) => {
                            let content = jsonResult[filename];
                            if (typeof content === 'object') {
                                content = JSON.stringify(content, null, 2);
                            }
                            return (
                                <pre key={filename}>
                                    {filename}{"\n"}{content}{"\n\n"}
                                </pre>
                            );
                        })
                    : result !== null && renderState !== 'fetching' && (
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{result}</pre>
                    )
                }




            </div>
        );
    }
}

export default Rendition;
